<script setup lang="ts">
import EditConversationModalDialogContent from '@/components/assistant/EditConversationModalDialogContent.vue'
import SquaredIconButton from '@/components/assistant/SquaredIconButton.vue'
import type {Conversation} from '@/client/mentor/ConversationsClient'
import type {ConversationIntent} from '@/components/assistant/types'
import {CONVERSATION_LIST_NOW_INTERVAL_MS} from '@/components/assistant/constants'
import {DATE_FMT, useDateTimeFormatter} from '@/composable/useDateTimeFormatter'
import {computed} from 'vue'
import {useAssistantStore} from '@/stores/assistant'
import {useAssistantViewport} from '@/components/assistant/composables/useAssistantViewport'
import {useConversationTitleFormatter} from '@/components/assistant/composables/useConversationTitleFormatter'
import {useDeleteConversation} from '@/components/assistant/composables/useDeleteConversation'
import {useEditConversation} from '@/components/assistant/composables/useEditConversation'
import {useI18n} from 'vue-i18n'
import {useNow} from '@vueuse/core'
import {useAssistantDebugSettings} from '@/components/assistant/composables/useAssistantDebugSettings'

interface TitledConversation {
  conversation: Conversation
  title: string
}

const [formatDate] = useDateTimeFormatter(DATE_FMT)
const [formatTitle] = useConversationTitleFormatter()
const assistantStore = useAssistantStore()
const {isDesktopVersion} = useAssistantViewport()
const {showDebugDetails} = useAssistantDebugSettings()
const {t} = useI18n()

const props = defineProps<{
  conversations: Conversation[]
  hasNextPage: boolean
  isFetchingNextPage: boolean
  intent: ConversationIntent
}>()

const {openDeleteConversationModal} = useDeleteConversation(props.intent.deleteConversationById)
const {editConversationModalId, editTitle, editTitleError, openEditConversationModal} = useEditConversation(
  props.intent.patchConversationTitle
)

const emits = defineEmits<{
  conversationSelected: [conversationId: string]
}>()

const nowMinutely = useNow({interval: CONVERSATION_LIST_NOW_INTERVAL_MS})

const dateLabelFormatter = computed(() => (date: Date) => {
  if (date.toDateString() === nowMinutely.value.toDateString()) {
    return t('assistant.conversation.dateLabels.today')
  } else {
    return formatDate.value.format(date)
  }
})

const sortedConversations = computed<TitledConversation[]>(() =>
  props.conversations
    .map((conversation) => ({
      conversation,
      title: formatTitle.value(conversation),
    }))
    .sort((a, b) => b.conversation.createdAt.getTime() - a.conversation.createdAt.getTime())
)

const groupedConversations = computed(() =>
  sortedConversations.value.reduce((groups, item) => {
    const dateLabel = dateLabelFormatter.value(item.conversation.createdAt)
    if (!groups.has(dateLabel)) {
      groups.set(dateLabel, [])
    }
    groups.get(dateLabel)!.push(item)
    return groups
  }, new Map<string, TitledConversation[]>())
)

function onConversationSelected(conversation: Conversation) {
  assistantStore.setActiveConversation(conversation)
  emits('conversationSelected', conversation.id)
}
</script>

<template>
  <div class="flex flex-col py-2 grow overflow-y-auto">
    <template v-for="group in groupedConversations.entries()" :key="group[0]">
      <!-- Date group header -->
      <div class="px-3 py-1 flex items-center">
        <span class="text-xs font-bold text-new-neutral-400 uppercase tracking-wide">{{ group[0] }}</span>
      </div>

      <!-- Conversations in group -->
      <button
        v-for="item in group[1]"
        :key="item.conversation.id"
        class="w-full h-11 min-w-0 flex flex-row items-center gap-2 px-3 group hover:bg-new-tertiary-100"
        @click="onConversationSelected(item.conversation)"
      >
        <!-- Conversation title -->
        <div
          class="py-2 flex-1 min-w-0 overflow-hidden text-left"
          :class="{'text-new-secondary-default font-semi-bold': assistantStore.activeConversationId === item.conversation.id}"
        >
          <slot name="title" :title="item.title">
            <span :class="{'block w-full truncate': isDesktopVersion}">
              {{ item.title }}
            </span>
          </slot>
          <span v-if="showDebugDetails" class="block w-full truncate text-xs font-normal text-new-neutral-400">
            {{ item.conversation.id }}
          </span>
        </div>

        <!-- Optional notification sign -->
        <div
          v-if="assistantStore.hasUnreadConversations && assistantStore.unreadConversationIds.has(item.conversation.id)"
          class="ml-auto w-1.5 h-1.5 rounded-full bg-new-secondary-default group-hover:hidden"
        ></div>

        <!-- Optional action slots -->
        <div v-if="isDesktopVersion" class="ml-auto hidden group-hover:flex gap-1 shrink-0" @click.stop>
          <SquaredIconButton
            v-tt="t('assistant.actions.edit')"
            icon-classes="fa-solid fa-pen"
            variant="light"
            @click="openEditConversationModal(item.conversation, item.title)"
          />

          <SquaredIconButton
            v-tt="t('assistant.actions.delete')"
            icon-classes="fa-solid fa-trash"
            variant="error"
            @click="openDeleteConversationModal(item.conversation, item.title)"
          />
        </div>
      </button>
    </template>

    <!-- Load more button -->
    <button
      v-if="hasNextPage"
      class="px-3 text-xs text-left text-new-neutral-400 hover:text-new-neutral-600 disabled:opacity-50"
      :disabled="isFetchingNextPage"
      @click="intent.fetchNextPage"
    >
      {{ isFetchingNextPage ? t('assistant.conversation.loadingMore') : t('assistant.conversation.loadMore') }}
    </button>
  </div>

  <!-- Edit conversation modal -->
  <EditConversationModalDialogContent
    v-if="editConversationModalId"
    :id="editConversationModalId"
    v-model="editTitle"
    :error="editTitleError"
  />
</template>
